export const licenseStatusColor = (status) => {
  const value = String(status || '').toLowerCase();

  if (value === 'active') return 'success';
  if (value === 'expiring') return 'warning';
  if (value === 'over_limit') return 'error';
  if (value === 'expired') return 'error';
  if (value === 'future') return 'info';
  if (value === 'no_license') return 'default';

  return 'default';
};

export const licenseStatusLabel = (status) => {
  const value = String(status || '').toLowerCase();

  if (value === 'no_license') return 'No License';
  if (value === 'over_limit') return 'Over Limit';
  if (value === 'expiring') return 'Expiring';
  if (value === 'expired') return 'Expired';
  if (value === 'future') return 'Future';
  if (value === 'past') return 'Past';
  if (value === 'active') return 'Active';

  return value || 'Unknown';
};

export const formatLicenseWindow = (license) => {
  if (!license?.valid_from || !license?.valid_to) {
    return 'Not scheduled';
  }

  return `${license.valid_from} to ${license.valid_to}`;
};

export const formatLicenseSummary = (usage) => {
  if (!usage) {
    return 'Not available';
  }

  if (usage.limit == null) {
    return usage.used == null ? 'Not configured' : `${usage.used} used / unlimited`;
  }

  return `${usage.used ?? 0} used / ${usage.limit} total`;
};

export const normalizeLicenseRole = (value) => String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_') || 'employee';

const isActiveSeatStatus = (status) => String(status || '').trim().toLowerCase() === 'active';

const findLatestPastLicense = (payload) => {
  const history = Array.isArray(payload?.license_history) ? payload.license_history : [];

  return history
    .filter((license) => String(license?.window_status || '').toLowerCase() === 'past')
    .sort((left, right) => String(right?.valid_to || '').localeCompare(String(left?.valid_to || '')))[0] || null;
};

export const buildMissingLicenseMessage = (payload) => {
  const latestPastLicense = findLatestPastLicense(payload);
  if (latestPastLicense?.valid_to) {
    return `The current tenant license expired on ${latestPastLicense.valid_to}. Create or renew a license before provisioning more users.`;
  }

  const nextLicense = payload?.next_license;
  if (nextLicense?.valid_from) {
    return `The next tenant license starts on ${nextLicense.valid_from}. Provisioning is blocked until that license becomes active.`;
  }

  return 'This tenant does not have an active license. Create a tenant license before provisioning more users.';
};

export const getRoleSeatUsage = (payload, role) => {
  const roleKey = normalizeLicenseRole(role);
  const usage = Array.isArray(payload?.license_usage?.per_role)
    ? payload.license_usage.per_role.find((item) => normalizeLicenseRole(item?.role) === roleKey)
    : null;

  if (usage) {
    return {
      role: roleKey,
      used: Number.isFinite(Number(usage.used)) ? Number(usage.used) : 0,
      limit: Number.isFinite(Number(usage.limit)) ? Number(usage.limit) : null,
      remaining: Number.isFinite(Number(usage.remaining)) ? Number(usage.remaining) : null,
      over_limit: Boolean(usage.over_limit),
    };
  }

  const currentLicenseRoleLimit = Array.isArray(payload?.current_license?.role_limits)
    ? payload.current_license.role_limits.find((item) => normalizeLicenseRole(item?.role) === roleKey)
    : null;

  if (!currentLicenseRoleLimit) {
    return null;
  }

  return {
    role: roleKey,
    used: 0,
    limit: Number(currentLicenseRoleLimit.active_user_limit),
    remaining: Number(currentLicenseRoleLimit.active_user_limit),
    over_limit: false,
  };
};

export const evaluateLicenseProvisioning = (payload, options = {}) => {
  const currentLicense = payload?.current_license || null;
  const usage = payload?.license_usage || {};
  const existingUser = options?.existingUser || null;
  const targetRole = normalizeLicenseRole(options?.targetRole || existingUser?.role || 'employee');
  const targetStatus = options?.targetStatus || existingUser?.status || 'active';
  const currentRole = normalizeLicenseRole(existingUser?.role || 'employee');
  const currentIsActive = isActiveSeatStatus(existingUser?.status);
  const targetIsActive = isActiveSeatStatus(targetStatus);

  const activeDelta = (targetIsActive ? 1 : 0) - (currentIsActive ? 1 : 0);
  let roleDelta = 0;

  if (targetIsActive) {
    roleDelta += 1;
  }

  if (currentIsActive && currentRole === targetRole) {
    roleDelta -= 1;
  }

  const messages = [];

  if (activeDelta > 0 && !currentLicense) {
    messages.push(buildMissingLicenseMessage(payload));
  }

  const currentActiveUsers = Number.isFinite(Number(usage.used)) ? Number(usage.used) : 0;
  const totalLimit = currentLicense?.total_active_users_limit != null
    ? Number(currentLicense.total_active_users_limit)
    : (usage.limit != null ? Number(usage.limit) : null);
  const projectedActiveUsers = currentActiveUsers + Math.max(activeDelta, 0);

  if (activeDelta > 0 && currentLicense && totalLimit != null && projectedActiveUsers > totalLimit) {
    messages.push(
      `Cannot provision an additional active user. Active users would become ${projectedActiveUsers}, but the current license allows ${totalLimit} active users from ${currentLicense.valid_from} to ${currentLicense.valid_to}.`
    );
  }

  const roleSeatUsage = getRoleSeatUsage(payload, targetRole);
  const projectedRoleUsers = (roleSeatUsage?.used ?? 0) + Math.max(roleDelta, 0);

  if (roleDelta > 0 && roleSeatUsage?.limit != null && projectedRoleUsers > roleSeatUsage.limit) {
    messages.push(
      `Cannot increase active ${targetRole} seats. Active ${targetRole} users would become ${projectedRoleUsers}, but the current license allows ${roleSeatUsage.limit}.`
    );
  }

  return {
    allowed: messages.length === 0,
    blocked: messages.length > 0,
    messages,
    activeDelta,
    roleDelta,
    projectedActiveUsers,
    projectedRoleUsers,
    roleSeatUsage,
    targetRole,
    targetStatus,
  };
};
